import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Button from '../../components/ui/Button'
import Modal from '../../components/ui/Modal'
import { api } from '../../lib/api'
import type { ProfileListItem, JobListItem } from '../../types/api'

type KanbanColumn = 'Shortlisted' | 'Submitted' | 'Interviewing' | 'Placed'

type PipelineCard = {
  id: string
  candidateName: string
  candidateId: string
  jobTitle: string
  jobId: string
  column: KanbanColumn
}

const COLUMNS: KanbanColumn[] = ['Shortlisted', 'Submitted', 'Interviewing', 'Placed']

export default function HeadhunterPipeline() {
  const navigate = useNavigate()
  const [cards, setCards] = useState<PipelineCard[]>([])
  const [showAdd, setShowAdd] = useState(false)
  const [profiles, setProfiles] = useState<ProfileListItem[]>([])
  const [jobs, setJobs] = useState<JobListItem[]>([])
  const [selectedProfile, setSelectedProfile] = useState('')
  const [selectedJob, setSelectedJob] = useState('')
  const [addLoading, setAddLoading] = useState(false)

  const openAdd = async () => {
    setAddLoading(true)
    try {
      const [p, j] = await Promise.all([api.profiles.list(), api.jobs.list()])
      setProfiles(p.data)
      setJobs(j.data)
      setSelectedProfile('')
      setSelectedJob('')
      setShowAdd(true)
    } finally {
      setAddLoading(false)
    }
  }

  const addCard = () => {
    if (!selectedProfile || !selectedJob) return
    const candidate = profiles.find((p) => p.id === selectedProfile)
    const job = jobs.find((j) => j.id === selectedJob)
    if (!candidate || !job) return
    setCards((prev) => [
      ...prev,
      {
        id: `${selectedProfile}-${selectedJob}-${Date.now()}`,
        candidateName: candidate.name,
        candidateId: selectedProfile,
        jobTitle: job.title,
        jobId: selectedJob,
        column: 'Shortlisted',
      },
    ])
    setShowAdd(false)
  }

  const moveCard = (cardId: string, to: KanbanColumn) => {
    setCards((prev) => prev.map((c) => c.id === cardId ? { ...c, column: to } : c))
  }

  const removeCard = (cardId: string) => {
    setCards((prev) => prev.filter((c) => c.id !== cardId))
  }

  return (
    <>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-heading tracking-tactical">PIPELINE</h1>
          <Button variant="primary" onClick={openAdd} disabled={addLoading}>
            {addLoading ? 'LOADING...' : 'ADD CANDIDATE'}
          </Button>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {COLUMNS.map((col) => {
            const colCards = cards.filter((c) => c.column === col)
            return (
              <div key={col} className="bg-surface-low border border-border-subtle">
                <div className="px-3 py-2 border-b border-border-subtle">
                  <p className="text-xs tracking-tactical text-text-muted font-normal">{col.toUpperCase()}</p>
                  <p className="text-lg font-heading text-foreground">{colCards.length}</p>
                </div>
                <div className="p-2 space-y-2 min-h-24">
                  {colCards.map((card) => (
                    <div key={card.id} className="bg-background border border-border-subtle p-3 space-y-2">
                      <button
                        onClick={() => navigate(`/headhunter/candidates/${card.candidateId}`)}
                        className="text-sm font-medium text-foreground hover:text-primary transition-colors text-left w-full"
                      >
                        {card.candidateName}
                      </button>
                      <p className="text-xs text-text-muted">{card.jobTitle}</p>
                      <div className="flex gap-1 flex-wrap">
                        {COLUMNS.filter((c) => c !== card.column).map((c) => (
                          <button
                            key={c}
                            onClick={() => moveCard(card.id, c)}
                            className="text-xs text-text-muted hover:text-primary transition-colors"
                          >
                            → {c}
                          </button>
                        ))}
                        <button
                          onClick={() => removeCard(card.id)}
                          className="text-xs text-red-400 hover:text-red-300 transition-colors ml-auto"
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )
          })}
        </div>
      </div>

      <Modal isOpen={showAdd} onClose={() => setShowAdd(false)} title="ADD TO PIPELINE">
        <div className="space-y-4">
          <div>
            <label className="block text-xs text-text-muted tracking-tactical mb-1">CANDIDATE</label>
            <select
              value={selectedProfile}
              onChange={(e) => setSelectedProfile(e.target.value)}
              className="w-full bg-background border border-border-subtle text-foreground text-sm px-3 py-2 focus:outline-none focus:border-primary"
            >
              <option value="">Select candidate...</option>
              {profiles.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-text-muted tracking-tactical mb-1">JOB</label>
            <select
              value={selectedJob}
              onChange={(e) => setSelectedJob(e.target.value)}
              className="w-full bg-background border border-border-subtle text-foreground text-sm px-3 py-2 focus:outline-none focus:border-primary"
            >
              <option value="">Select job...</option>
              {jobs.map((j) => <option key={j.id} value={j.id}>{j.title} — {j.employer}</option>)}
            </select>
          </div>
          <div className="flex gap-2 pt-2">
            <Button onClick={addCard} disabled={!selectedProfile || !selectedJob}>ADD</Button>
            <Button variant="ghost" onClick={() => setShowAdd(false)}>CANCEL</Button>
          </div>
        </div>
      </Modal>
    </>
  )
}
